interface ColorSet {
  main: string
  hover: string
  light: string
  dark: string
  background: string
  backgroundBase: string // Nuevo valor para el fondo base
  text: string
  contrast: string
}

// Convierte un color hex a RGB
const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return { r, g, b }
}

// Convierte RGB a HSL
const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }

  return { h: h * 360, s: s * 100, l: l * 100 }
}

// Convierte HSL a RGB
const hslToRgb = (h: number, s: number, l: number): { r: number; g: number; b: number } => {
  h /= 360
  s /= 100
  l /= 100

  let r: number
  let g: number
  let b: number

  if (s === 0) {
    r = g = b = l
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q

    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  }
}

// Convierte RGB a Hex
const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (n: number) => {
    const hex = n.toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

// Genera una paleta armoniosa basada en un color
export const adjustColor = (hex: string, mode: 'light' | 'dark'): ColorSet => {
  const rgb = hexToRgb(hex)
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b)

  // Ajustes para modo claro
  if (mode === 'light') {
    // Color principal
    const main = hex

    // Color hover (ligeramente más oscuro)
    const hoverHsl = { ...hsl, l: Math.max(0, hsl.l - 10) }
    const hoverRgb = hslToRgb(hoverHsl.h, hoverHsl.s, hoverHsl.l)
    const hover = rgbToHex(hoverRgb.r, hoverRgb.g, hoverRgb.b)

    // Color claro (versión más suave)
    const lightHsl = { ...hsl, s: Math.max(0, hsl.s - 30), l: Math.min(95, hsl.l + 35) }
    const lightRgb = hslToRgb(lightHsl.h, lightHsl.s, lightHsl.l)
    const light = rgbToHex(lightRgb.r, lightRgb.g, lightRgb.b)

    // Color oscuro (versión más intensa)
    const darkHsl = { ...hsl, s: Math.min(100, hsl.s + 10), l: Math.max(0, hsl.l - 20) }
    const darkRgb = hslToRgb(darkHsl.h, darkHsl.s, darkHsl.l)
    const dark = rgbToHex(darkRgb.r, darkRgb.g, darkRgb.b)

    // Fondo base (muy suave)
    const bgBaseHsl = { ...hsl, s: Math.max(0, hsl.s - 40), l: 98 }
    const bgBaseRgb = hslToRgb(bgBaseHsl.h, bgBaseHsl.s, bgBaseHsl.l)
    const backgroundBase = rgbToHex(bgBaseRgb.r, bgBaseRgb.g, bgBaseRgb.b)

    return {
      main,
      hover,
      light,
      dark,
      background: '#ffffff',
      backgroundBase,
      text: '#1a1a1a',
      contrast: '#ffffff'
    }
  }

  // Ajustes para modo oscuro
  else {
    // Color principal (más brillante para modo oscuro)
    const mainHsl = { ...hsl, l: Math.min(100, hsl.l + 15) }
    const mainRgb = hslToRgb(mainHsl.h, mainHsl.s, mainHsl.l)
    const main = rgbToHex(mainRgb.r, mainRgb.g, mainRgb.b)

    // Color hover (ligeramente más brillante)
    const hoverHsl = { ...mainHsl, l: Math.min(100, mainHsl.l + 10) }
    const hoverRgb = hslToRgb(hoverHsl.h, hoverHsl.s, hoverHsl.l)
    const hover = rgbToHex(hoverRgb.r, hoverRgb.g, hoverRgb.b)

    // Color claro (muy brillante pero con tinte)
    const lightHsl = { ...hsl, s: Math.max(15, hsl.s - 20), l: Math.min(95, hsl.l + 40) }
    const lightRgb = hslToRgb(lightHsl.h, lightHsl.s, lightHsl.l)
    const light = rgbToHex(lightRgb.r, lightRgb.g, lightRgb.b)

    // Color oscuro (versión más oscura para contraste)
    const darkHsl = { ...hsl, s: Math.min(100, hsl.s + 10), l: Math.max(0, hsl.l - 30) }
    const darkRgb = hslToRgb(darkHsl.h, darkHsl.s, darkHsl.l)
    const dark = rgbToHex(darkRgb.r, darkRgb.g, darkRgb.b)

    // Fondo oscuro con un toque del color primario
    const backgroundHsl = { ...hsl, s: Math.max(10, hsl.s - 60), l: Math.max(8, hsl.l - 85) }
    const backgroundRgb = hslToRgb(backgroundHsl.h, backgroundHsl.s, backgroundHsl.l)
    const background = rgbToHex(backgroundRgb.r, backgroundRgb.g, backgroundRgb.b)

    // Fondo base para modo oscuro (más suave que el negro puro)
    const bgBaseHsl = { ...hsl, s: 15, l: 12 }
    const bgBaseRgb = hslToRgb(bgBaseHsl.h, bgBaseHsl.s, bgBaseHsl.l)
    const backgroundBase = rgbToHex(bgBaseRgb.r, bgBaseRgb.g, bgBaseRgb.b)

    // Texto con ligero tinte del color primario
    const textHsl = { ...hsl, s: 10, l: 95 }
    const textRgb = hslToRgb(textHsl.h, textHsl.s, textHsl.l)
    const text = rgbToHex(textRgb.r, textRgb.g, textRgb.b)

    // Contraste con tinte sutil
    const contrastHsl = { ...hsl, s: 5, l: 98 }
    const contrastRgb = hslToRgb(contrastHsl.h, contrastHsl.s, contrastHsl.l)
    const contrast = rgbToHex(contrastRgb.r, contrastRgb.g, contrastRgb.b)

    return {
      main,
      hover,
      light,
      dark,
      background,
      backgroundBase,
      text,
      contrast
    }
  }
}
